import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Check, X, AlertCircle, Gift } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { GoogleSignInButton } from '../components/Auth/GoogleSignInButton';
import { useAuth } from '../contexts/AuthContext';
import { trackCriticalEvent } from '../utils/mixpanelTracking';
import { trackLogin, trackSignup } from '../utils/analytics';
import { trackEvent, trackEventOnce, trackCtaClick } from '../utils/visitorTracking';
import { ensureProfile } from '../services/profileService';
import { clearStoredReferralCode, getStoredReferralCode, storeReferralCode } from '../utils/referral';
import { clearSelectedPlan, getSelectedPlan, normalizePlanParam, storeSelectedPlan } from '../utils/selectedPlan';
import { redirectToCheckout, startCheckout } from '../services/checkoutService';
import { markWelcomeModalPending } from '../utils/upgradePrompts';
export function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signInWithGoogle
  } = useAuth();
  const getInitialView = (): 'login' | 'signup' | 'forgot' => {
    if (location.pathname === '/login') return 'login';
    if (location.pathname === '/signup') return 'signup';
    return 'signup';
  };
  const [view, setView] = useState<'login' | 'signup' | 'forgot'>(getInitialView());
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [resetOtpVerified, setResetOtpVerified] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);
  // A referral link (/signup?ref=CODE) is captured on load and preserved
  // through signup. Reward validation happens server-side.
  useEffect(() => {
    const incoming = new URLSearchParams(location.search).get('ref');
    if (incoming) {
      storeReferralCode(incoming);
      setReferralCode(incoming);
      return;
    }
    setReferralCode(getStoredReferralCode());
  }, [location.search]);
  // A plan chosen before authenticating (/signup?plan=monthly) is preserved so
  // checkout resumes the moment the account is ready.
  useEffect(() => {
    const incomingPlan = normalizePlanParam(new URLSearchParams(location.search).get('plan'));
    if (incomingPlan) storeSelectedPlan(incomingPlan);
  }, [location.search]);
  /**
   * Continues an interrupted checkout with the plan the visitor selected
   * before authenticating. Falls back to the normal destination.
   */
  const resumeCheckoutOrNavigate = async (fallback: string) => {
    const plan = getSelectedPlan();
    if (!plan) {
      navigate(fallback);
      return;
    }
    // A single-report unlock needs a finished scan, so it can't resume here.
    if (plan === 'single_scan') {
      clearSelectedPlan();
      navigate(fallback);
      return;
    }
    const result = await startCheckout({
      plan,
      location: 'auth_resume'
    });
    if (!result.ok) {
      console.error('Resume checkout failed:', result.code);
      navigate(fallback);
      return;
    }
    clearSelectedPlan();
    redirectToCheckout(result.authorizationUrl);
  };
  // Helper function to format error messages with troubleshooting tips
  const formatError = (err: any): string => {
    const errorMessage = err.message || err.toString();
    // Database error saving new user - trigger failure
    if (errorMessage.includes('Database error saving new user') || errorMessage.includes('database error') || errorMessage.includes('Database error')) {
      return 'Account setup error: There was a problem creating your account. Please try again. If the issue persists, try using Google Sign-In instead.';
    }
    // Network/fetch errors
    if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError') || errorMessage.includes('fetch')) {
      return 'Connection error: Unable to reach authentication server. Please check your internet connection and try again. If using a VPN or ad blocker, try disabling it temporarily.';
    }
    // CORS errors
    if (errorMessage.includes('CORS') || errorMessage.includes('cross-origin')) {
      return 'Connection blocked: Your browser is blocking the authentication request. Please check your browser extensions or try a different browser.';
    }
    // Timeout errors
    if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
      return 'Request timeout: The server took too long to respond. Please check your internet connection and try again.';
    }
    // Return original message for other errors
    return errorMessage;
  };
  useEffect(() => {
    if (location.pathname === '/login') {
      setView('login');
      resetForm();
    } else if (location.pathname === '/signup') {
      setView('signup');
      resetForm();
    }
  }, [location.pathname]);
  const validatePassword = (pwd: string) => {
    const requirements = {
      minLength: pwd.length >= 8,
      hasUpperCase: /[A-Z]/.test(pwd),
      hasNumber: /[0-9]/.test(pwd),
      hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    };
    return requirements;
  };
  const passwordRequirements = validatePassword(newPassword || password);
  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const resetForm = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setOtp('');
    setOtpSent(false);
    setResetOtpVerified(false);
    setError(null);
    setSuccess(null);
    setShowPasswordRequirements(false);
  };
  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);
    // Track Google OAuth initiation
    await trackCriticalEvent('Google_OAuth_Initiated', {
      auth_action: view === 'signup' ? 'signup' : 'login',
      page_location: 'auth_page'
    });
    // First-party: a Google signup begins here, at the click — not on mount.
    // Logins are excluded so the funnel only counts real signup intent.
    if (view === 'signup') {
      void trackEvent('signup_started', {
        method: 'google'
      });
    }
    try {
      await signInWithGoogle();
      // User will be redirected to Google OAuth
    } catch (err: any) {
      console.error('Google sign-in error:', err);
      setError(formatError(err));
      setGoogleLoading(false);
    }
  };
  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      return;
    }
    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      setShowPasswordRequirements(true);
      return;
    }
    setLoading(true);
    trackCtaClick('Create Account', 'auth_page_signup_form', '/onboarding');
    void trackEvent('signup_started', {
      method: 'email'
    });
    try {
      const {
        data,
        error
      } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            fullName: `${firstName.trim()} ${lastName.trim()}`,
            first_name: firstName.trim(),
            last_name: lastName.trim(),
            full_name: `${firstName.trim()} ${lastName.trim()}`,
            ...(referralCode ? {
              referral_code: referralCode
            } : {})
          }
        }
      });
      if (error) throw error;
      if (data?.user?.identities?.length === 0) {
        setError('An account with this email already exists');
        return;
      }
      // Fire-and-forget: Call Cloudflare bot protect edge function
      supabase.functions.invoke('cloudflare-bot-protect').catch(err => {
        console.error('[Bot Protection] Edge function failed silently:', err);
      });
      // Supabase sends a 6-digit OTP code to the user's email
      // Switch to the OTP input screen so the user can verify
      setOtpSent(true);
      setSuccess('Verification code sent! Please check your email and enter the 6-digit code below.');
    } catch (err: any) {
      console.error('Signup error:', err);
      console.error('Signup error code:', err?.code, '| status:', err?.status, '| message:', err?.message);
      if (err?.message?.toLowerCase().includes('unexpected') || err?.code === 'unexpected_failure') {
        console.warn('[KNOWN ISSUE] Unexpected Failure detected during signup. ' + 'The user may have been created successfully despite this error. ' + 'Check the Authentication tab in Supabase to verify. ' + 'This was previously caused by broken Welcome Email triggers (now purged).');
      }
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        data,
        error
      } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'signup'
      });
      if (error) throw error;
      if (data.session) {
        // Ensure profile exists in the profiles table (safety net for trigger failures)
        const userId = data.session.user.id;
        await ensureProfile(userId, {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          fullName: `${firstName.trim()} ${lastName.trim()}`,
          email: email
        });
        // Track successful signup
        const emailDomain = email.split('@')[1];
        await trackCriticalEvent('User_Signed_Up', {
          signup_method: 'email',
          first_name: firstName,
          last_name: lastName,
          email_domain: emailDomain,
          has_password_requirements_met: true
        });
        // GA4 only learns that a signup happened — no email, name or tokens.
        trackSignup('email');
        // First-party: only fires after auth + profile creation actually
        // succeeded, and only once per user id.
        void trackEventOnce(`signup_completed:${userId}`, 'signup_completed', {
          user_id: userId,
          method: 'email'
        });
        // The referral code has been handed to the backend with the signup
        clearStoredReferralCode();
        // Greet the new account once, on their first dashboard visit
        markWelcomeModalPending();
        // New users go to onboarding first — unless they came in mid-checkout
        await resumeCheckoutOrNavigate('/onboarding');
      }
    } catch (err: any) {
      console.error('OTP verification error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    trackCtaClick('Login', 'auth_page_login_form', '/dashboard');
    try {
      const {
        data,
        error
      } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      if (error) throw error;
      if (data.session) {
        // Track successful login
        await trackCriticalEvent('User_Logged_In', {
          login_method: 'email',
          is_returning_user: true
        });
        trackLogin('email');
        await resumeCheckoutOrNavigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleSendResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`
      });
      if (error) throw error;
      // Track password reset request
      const emailDomain = email.split('@')[1];
      await trackCriticalEvent('Password_Reset_Requested', {
        email_domain: emailDomain
      });
      setOtpSent(true);
      setSuccess('Reset code sent! Please check your email and enter the code below.');
    } catch (err: any) {
      console.error('Reset password error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleVerifyResetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery'
      });
      if (error) throw error;
      setResetOtpVerified(true);
      setSuccess('Code verified! Now enter your new password.');
      setOtp('');
    } catch (err: any) {
      console.error('Reset OTP verification error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isPasswordValid) {
      setError('Password does not meet requirements');
      setShowPasswordRequirements(true);
      return;
    }
    setLoading(true);
    try {
      const {
        error
      } = await supabase.auth.updateUser({
        password: newPassword
      });
      if (error) throw error;
      // Track password reset completion
      await trackCriticalEvent('Password_Reset_Completed', {
        reset_method: 'otp_verification'
      });
      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        setView('login');
        resetForm();
      }, 2000);
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  };
  return <div className="flex flex-col w-full min-h-screen bg-[#f8f6f2]" data-id="element-3522" data-mp="1k9rie0uidlcm">
      <header className="bg-white border-b border-gray-200" data-id="element-3523" data-mp="1f9wso0mgz1t5">
        <div className="max-w-md mx-auto px-5 py-4" data-id="element-3524" data-mp="1fbgd83f5rz0q">
          <Link to="/" className="inline-flex items-center text-gray-600 hover:text-purple-600 text-sm" data-id="element-3525" data-mp="1abbtm4iyeo6a">
            <ArrowLeft size={16} className="mr-1" data-id="element-3526" data-mp="3lzqdb8dh8cli" />
            Back to Home
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-5 py-12" data-id="element-3527" data-mp="3vvto12me9jk2">
        <div className="w-full max-w-md" data-id="element-3528" data-mp="3vtpbxxecde8p">
          <div className="bg-white rounded-xl shadow-sm p-8" data-id="element-3529" data-mp="3sj00tikwff0">
            <h1 className="text-2xl font-bold mb-2 text-center" data-id="element-3530" data-mp="3pok9faifirx">
              {view === 'signup' ? 'Create Account' : view === 'login' ? 'Welcome Back' : 'Reset Password'}
            </h1>
            <p className="text-gray-600 text-sm text-center mb-6" data-id="element-3531" data-mp="3c715cnh4xgux">
              {view === 'signup' ? 'Start analyzing conversations for toxic patterns' : view === 'login' ? 'Sign in to your ToxicDetector account' : !otpSent ? 'Enter your email to receive a reset code' : !resetOtpVerified ? 'Enter the code sent to your email' : 'Create your new password'}
            </p>

            {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4 text-sm flex items-start gap-2" data-id="element-3532" data-mp="5dl1h6qxi5gb">
                <AlertCircle size={18} className="flex-shrink-0 mt-0.5" data-id="element-3533" data-mp="fj89szsgpr4" />
                <div className="flex-1" data-id="element-3534" data-mp="38u1km6wrirgj">{error}</div>
              </div>}

            {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-4 text-sm" data-id="element-3535" data-mp="33w051vyjpa9t">
                {success}
              </div>}

            {/* Google Sign-In Button - Show on login and signup (not on forgot password or OTP) */}
            {view !== 'forgot' && !otpSent && <>
                <GoogleSignInButton onClick={handleGoogleSignIn} loading={googleLoading} text={view === 'signup' ? 'Sign up with Google' : 'Sign in with Google'} data-id="element-3536" data-mp="x6twakiqykgn" />

                <div className="relative my-6" data-id="element-3537" data-mp="qoic2885b87k">
                  <div className="absolute inset-0 flex items-center" data-id="element-3538" data-mp="qpse18i4d0jh">
                    <div className="w-full border-t border-gray-300" data-id="element-3539" data-mp="vn1ye9qnim0g"></div>
                  </div>
                  <div className="relative flex justify-center text-sm" data-id="element-3540" data-mp="1jcmkh1i3pzdy">
                    <span className="px-2 bg-white text-gray-500" data-id="element-3541" data-mp="1jfijyu9lkw4i">
                      Or continue with email
                    </span>
                  </div>
                </div>
              </>}

            {view === 'signup' && !otpSent && <form onSubmit={handleSignup} className="space-y-4" data-id="element-3542" data-mp="1ekv2vggxb3zy">
                <div className="grid grid-cols-2 gap-3" data-id="element-3543" data-mp="22bs4o0f08kxh">
                  <div data-id="element-3544" data-mp="229xlornan2w1">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3545" data-mp="272gxbj2f7ny7">
                      First Name
                    </label>
                    <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3546" data-mp="7227v8muhbit" />
                  </div>
                  <div data-id="element-3547" data-mp="py23quiy0z7o">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3548" data-mp="px940diej9z1">
                      Last Name
                    </label>
                    <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3549" data-mp="gy5ht0nxuegb" />
                  </div>
                </div>

                <div data-id="element-3550" data-mp="2mmnuvgcoetbv">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3551" data-mp="2mnfnqjew2cyg">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3552" data-mp="2rimphloll7kq" />
                </div>

                <div data-id="element-3553" data-mp="3a9vzrx6700dd">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3554" data-mp="3ad9zexnh1vh4">
                    Password
                  </label>
                  <input type="password" value={password} onChange={e => {
                setPassword(e.target.value);
                setShowPasswordRequirements(true);
              }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3555" data-mp="3k9wk9mfssmj5" />
                </div>

                {showPasswordRequirements && <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm" data-id="element-3556" data-mp="6tehb2cbbb4r">
                    <p className="font-medium text-gray-700" data-id="element-3557" data-mp="bmppp7wrvzys">
                      Password requirements:
                    </p>
                    <div className="space-y-1" data-id="element-3558" data-mp="gkhis595ls4k">
                      <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3559" data-mp="ljc8lr5jggy6">
                        {passwordRequirements.minLength ? <Check size={16} className="mr-2" data-id="element-3560" data-mp="uk0etzooxd0i" /> : <X size={16} className="mr-2" data-id="element-3561" data-mp="2red9s2qoty06" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3562" data-mp="2os496s6f1g5x">
                        {passwordRequirements.hasUpperCase ? <Check size={16} className="mr-2" data-id="element-3563" data-mp="2f343yr1bloja" /> : <X size={16} className="mr-2" data-id="element-3564" data-mp="2a4sz6upvu47x" />}
                        One uppercase letter
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3565" data-mp="32sf01v7q6ur3">
                        {passwordRequirements.hasNumber ? <Check size={16} className="mr-2" data-id="element-3566" data-mp="2tqp8mpdufvtb" /> : <X size={16} className="mr-2" data-id="element-3567" data-mp="1meyn4fde0joq" />}
                        One number
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3568" data-mp="1ieodmsqdzb54">
                        {passwordRequirements.hasSpecialChar ? <Check size={16} className="mr-2" data-id="element-3569" data-mp="19u9riqqyw8fp" /> : <X size={16} className="mr-2" data-id="element-3570" data-mp="1etcve7v7o8m3" />}
                        One special character
                      </div>
                    </div>
                  </div>}

                {/* Referral field — only shown when a code was actually
                    detected from the link or a previous visit. */}
                {referralCode && <div data-id="element-3571" data-mp="w4y55mspwymc">
                    <label htmlFor="referral-code" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3572" data-mp="w4ejpw3uzs3k">
                      Referral code
                    </label>
                    <input id="referral-code" type="text" value={referralCode} onChange={e => {
                const next = e.target.value.trim().toUpperCase();
                setReferralCode(next);
                if (next) storeReferralCode(next);
              }} autoComplete="off" spellCheck={false} aria-describedby="referral-code-status" className="w-full px-4 py-2 border border-green-300 bg-green-50 rounded-lg font-mono tracking-widest uppercase text-gray-900 focus:ring-2 focus:ring-purple-600 focus:border-transparent" data-id="element-3573" data-mp="3hez77uzaujx" />
                    <div id="referral-code-status" className="mt-2 flex items-start gap-2 text-sm" data-id="element-3574" data-mp="r7ixczpee23j">
                      <Gift size={16} className="mt-0.5 flex-shrink-0 text-green-600" aria-hidden="true" data-id="element-3575" data-mp="108gwb3kzbjf9" />
                      <div data-id="element-3576" data-mp="1oalx14sj07o4">
                        <p className="font-medium text-green-700" data-id="element-3577" data-mp="1o9u6i8dsd04p">
                          🎁 Referral code applied
                        </p>
                        <p className="mt-0.5 text-gray-600" data-id="element-3578" data-mp="1y0x95emd5n63">
                          You can use this referral code during signup.
                        </p>
                      </div>
                    </div>
                  </div>}

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3579" data-mp="15ct2oqvyx62c">
                  {loading ? 'Creating Account...' : 'Create Account'}
                </button>

                <p className="text-center text-sm text-gray-600" data-id="element-3580" data-mp="2qokpghglprmx">
                  Already have an account?{' '}
                  <Link to="/login" className="text-purple-600 font-medium hover:underline" data-id="element-3581" data-mp="f1qzx24hdk51">
                    Sign in
                  </Link>
                </p>

                <p className="text-xs text-gray-500 text-center mt-4" data-id="element-3582" data-mp="5acukdpulq3z">
                  By signing up, you agree to our{' '}
                  <a href="/terms" className="text-purple-600 hover:underline" data-id="element-3583" data-mp="3shyf48eiwt7g">
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a href="/privacy" className="text-purple-600 hover:underline" data-id="element-3584" data-mp="3nkor8x6s8plq">
                    Privacy Policy
                  </a>
                </p>
              </form>}

            {view === 'signup' && otpSent && <form onSubmit={handleVerifyOtp} className="space-y-4" data-id="element-3585" data-mp="1gpi709fs730s">
                <div data-id="element-3586" data-mp="1btkteavwkarp">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3587" data-mp="1bqyo6hwhi7cn">
                    Verification Code
                  </label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-center text-2xl tracking-widest" placeholder="000000" maxLength={6} required data-id="element-3588" data-mp="3b0rssntlyk53" />
                </div>

                <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3589" data-mp="3fz2zwqsiqgjm">
                  {loading ? 'Verifying...' : 'Verify & Continue'}
                </button>

                <button type="button" onClick={() => {
              setOtpSent(false);
              setOtp('');
              setError(null);
              setSuccess(null);
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3590" data-mp="21177v1fj8l2l">
                  Back to signup
                </button>
              </form>}

            {view === 'login' && <form onSubmit={handleLogin} className="space-y-4" data-id="element-3591" data-mp="1dc5v51zo2nri">
                <div data-id="element-3592" data-mp="1ddz2ykm0ne13">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3593" data-mp="1dab9flgkmrmd">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3594" data-mp="1na23jm17z4lx" />
                </div>

                <div data-id="element-3595" data-mp="e1enxh51bt6z">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3596" data-mp="e0wk9zg1mcp1">
                    Password
                  </label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3597" data-mp="iyy26h1qg5yx" />
                </div>

                <div className="flex items-center justify-between text-sm" data-id="element-3598" data-mp="11s1x05qosov4">
                  <button type="button" onClick={() => setView('forgot')} className="text-purple-600 hover:underline" data-id="element-3599" data-mp="16qllacsr6cqn">
                    Forgot password?
                  </button>
                </div>

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3600" data-mp="308szcor8dxvu">
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>

                <p className="text-center text-sm text-gray-600" data-id="element-3601" data-mp="2mavy3f1u13au">
                  Don't have an account?{' '}
                  <Link to="/signup" className="text-purple-600 font-medium hover:underline" data-id="element-3602" data-mp="3j14yl2ml8j1r">
                    Sign up
                  </Link>
                </p>
              </form>}

            {view === 'forgot' && !otpSent && <form onSubmit={handleSendResetOtp} className="space-y-4" data-id="element-3603" data-mp="6hx03rkrch4m">
                <div data-id="element-3604" data-mp="6j84ntm11sws">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3605" data-mp="1r6vwho2ikbi">
                    Email
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3606" data-mp="3ppla6j5n8xfm" />
                </div>

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3607" data-mp="cjtibxgu58s1">
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>

                <button type="button" onClick={() => {
              setView('login');
              resetForm();
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3608" data-mp="hcmngmg7uj4n">
                  Back to login
                </button>
              </form>}

            {view === 'forgot' && otpSent && !resetOtpVerified && <form onSubmit={handleVerifyResetOtp} className="space-y-4" data-id="element-3609" data-mp="10wv0flzcd51x">
                <div data-id="element-3610" data-mp="15vg0nlelfoqd">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3611" data-mp="15uwqwrz3k6nf">
                    Reset Code
                  </label>
                  <input type="text" value={otp} onChange={e => setOtp(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent text-center text-2xl tracking-widest" placeholder="000000" maxLength={6} required data-id="element-3612" data-mp="r4z2cax99exp" />
                </div>

                <button type="submit" disabled={loading || otp.length !== 6} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3613" data-mp="d5pkz7xbces5">
                  {loading ? 'Verifying...' : 'Verify Code'}
                </button>

                <button type="button" onClick={() => {
              setOtpSent(false);
              setOtp('');
              setError(null);
              setSuccess(null);
            }} className="w-full text-gray-600 text-sm hover:text-purple-600" data-id="element-3614" data-mp="3bjf3l6jxajig">
                  Back to email entry
                </button>
              </form>}

            {view === 'forgot' && resetOtpVerified && <form onSubmit={handleResetPassword} className="space-y-4" data-id="element-3615" data-mp="2nu45jwtyj380">
                <div data-id="element-3616" data-mp="2itgkdrsetglp">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3617" data-mp="2iswy5yvq861v">
                    New Password
                  </label>
                  <input type="password" value={newPassword} onChange={e => {
                setNewPassword(e.target.value);
                setShowPasswordRequirements(true);
              }} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3618" data-mp="ya2ymu08bzo9" />
                </div>

                <div data-id="element-3619" data-mp="1lzozdlw92myd">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3620" data-mp="1r0lc8oykxjfx">
                    Confirm New Password
                  </label>
                  <input type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-600 focus:border-transparent" required data-id="element-3621" data-mp="1vyd3dgreh1j2" />
                </div>

                {showPasswordRequirements && <div className="bg-gray-50 p-3 rounded-lg space-y-2 text-sm" data-id="element-3622" data-mp="1n61qo26qhazt">
                    <p className="font-medium text-gray-700" data-id="element-3623" data-mp="212nlf4bayjq0">
                      Password requirements:
                    </p>
                    <div className="space-y-1" data-id="element-3624" data-mp="19cak92oau78b">
                      <div className={`flex items-center ${passwordRequirements.minLength ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3625" data-mp="19d2arz31herq">
                        {passwordRequirements.minLength ? <Check size={16} className="mr-2" data-id="element-3626" data-mp="1ddw1t1bs39ou" /> : <X size={16} className="mr-2" data-id="element-3627" data-mp="1dc1fpjortbj6" />}
                        At least 8 characters
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasUpperCase ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3628" data-mp="llwytv07z6mp">
                        {passwordRequirements.hasUpperCase ? <Check size={16} className="mr-2" data-id="element-3629" data-mp="gbaxlqnhneb4" /> : <X size={16} className="mr-2" data-id="element-3630" data-mp="1iqzx5j3xz12" />}
                        One uppercase letter
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasNumber ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3631" data-mp="uaaqjai1afd3">
                        {passwordRequirements.hasNumber ? <Check size={16} className="mr-2" data-id="element-3632" data-mp="kd63z9ka0hvr" /> : <X size={16} className="mr-2" data-id="element-3633" data-mp="pc97uqoisi25" />}
                        One number
                      </div>
                      <div className={`flex items-center ${passwordRequirements.hasSpecialChar ? 'text-green-600' : 'text-gray-500'}`} data-id="element-3634" data-mp="3a0wzk0uxnqwr">
                        {passwordRequirements.hasSpecialChar ? <Check size={16} className="mr-2" data-id="element-3635" data-mp="1bkodq04hbsve" /> : <X size={16} className="mr-2" data-id="element-3636" data-mp="16q0nnwttuqep" />}
                        One special character
                      </div>
                    </div>
                  </div>}

                <button type="submit" disabled={loading} className="w-full bg-purple-600 text-white py-3 rounded-lg font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-3637" data-mp="wrcivzqus7sy">
                  {loading ? 'Resetting Password...' : 'Reset Password'}
                </button>
              </form>}
          </div>
        </div>
      </div>
    </div>;
}